import { dayLabel, money } from './ui'
import { BEHIND_EARLIER, SIGNED_OUT_MESSAGE, WAITING_FOR_ACCOUNT, waitingForOtherAccounts, type QueuedMutation, type TheirChange } from './api'

// The words on the saving indicator, decided away from the DOM so they can be
// tested. Every line here is a CLAIM about the world, and the wrong claim
// sends someone to fix the wrong thing: "waiting for signal" on a change that
// is actually held behind an earlier edit to the same row sent Lewis outside
// looking for coverage, and an expired sign-in wearing the same words looked
// like a dead zone rather than a login.

export interface ChipSummary {
  text: string
  className: 'chip-conflict' | 'chip-queued' | 'chip-sending' | 'chip-clear'
}

// `elsewhere` counts changes on this phone that belong to OTHER accounts
// (another business, or the other login of this one): they cannot send until
// their own account signs in, and this one cannot see what they are.
export function chipSummary(
  q: readonly QueuedMutation[],
  persistNote: boolean,
  elsewhere: number = waitingForOtherAccounts(),
): ChipSummary {
  const sending = q.filter((m) => m.status === 'sending').length
  const queued = q.filter((m) => m.status === 'queued')
  // An intent held behind an earlier same-row edit is waiting for THAT edit,
  // not for a bar of signal.
  const waitingForSignal = queued.filter((m) => m.error !== BEHIND_EARLIER).length
  const attention = q.filter((m) => m.status === 'conflict' || m.status === 'failed').length
  const signedOut = q.filter((m) => m.status === 'signed-out').length
  // Changes the server said belong to a business other than the one signed
  // in: nothing else can send until that is sorted, so they rank above the
  // ordinary queue.
  const parked = q.filter((m) => m.status === 'parked').length
  const otherAccounts = (n: number): ChipSummary => ({
    text: `⚠ ${n} change${n > 1 ? 's' : ''} ${WAITING_FOR_ACCOUNT}`,
    className: 'chip-queued',
  })

  // Ranked above conflicts on purpose: while the session is gone nothing else
  // in the queue can move, so this is the one thing to act on.
  if (signedOut > 0) return { text: `✖ ${SIGNED_OUT_MESSAGE}`, className: 'chip-conflict' }
  // Plain words (Liam, 8 Oct 2026: the panel needed improving): what happened, not the mechanism.
  if (attention > 0) return { text: `✖ ${attention} change${attention > 1 ? 's' : ''} didn't save`, className: 'chip-conflict' }
  if (parked > 0) return otherAccounts(parked + elsewhere)
  if (queued.length > 0) {
    const noun = `${queued.length} change${queued.length > 1 ? 's' : ''}`
    return {
      className: 'chip-queued',
      text: persistNote
        ? `⚠ ${noun} waiting, not saved to this browser`
        : waitingForSignal > 0
          ? `⚠ ${noun} waiting for signal`
          : `⚠ ${noun} behind an earlier change`,
    }
  }
  if (sending > 0) return { text: `↑ saving ${sending}…`, className: 'chip-sending' }
  if (persistNote) return { text: '⚠ queue not saved to browser storage', className: 'chip-queued' }
  if (elsewhere > 0) return otherAccounts(elsewhere)
  // Small on purpose (Liam, 2026-09-25): a tick in the corner, not a box
  // over the page. The coloured states above stay full size. It claims only
  // what this indicator counts, the change queue: photos and voice notes
  // keep their own "not sent yet" line on the job (review 2026-09-25).
  return { text: '✓ Changes sent', className: 'chip-clear' }
}

// A waiting change's name as the owner reads it: any computer date in it ("Confirm booking: Ann 2026-10-10") said as
// a day ("Sat 10 Oct"). The stored name is unchanged, so nothing that matches on it moves (bug hunt 8 Oct 2026).
export function labelWords(label: string): string {
  return label.replace(/\b(\d{4}-\d{2}-\d{2})\b/g, (iso) => dayLabel(iso))
}

// A box's name as the screens call it, for "Lewis changed Phone to …" (same-time saves, 15 Oct 2026).
const BOX_WORDS: Record<string, string> = {
  extras_json: 'Extras',
  name: 'Name', area: 'Area', address: 'Address', postcode: 'Postcode', maps_link: 'Map link', phone: 'Phone', email: 'Email',
  origin: 'Where from', time_taken: 'Time taken', job_notes: 'Job notes', parking_restrictions: 'Parking',
  preferred_times: 'Preferred times', call_notes: 'Call notes', notes: 'Notes', notes2: 'Notes', initial_lead: 'Their message',
  to_call: 'Call?', said_note: 'What they said', message_by: 'How to message them', tier: 'Tier', status: 'Status',
  cycle_days: 'How often', cycle_text: 'How often', reg_price_pence: 'Regular price', first_clean_price_pence: 'First clean price',
  inside_price_pence: 'Inside price', payment_increase: 'Payment increase', note: 'Note', note_append: 'Note',
  duration_minutes: 'How long', date: 'Day', time_slot: 'Time', worker: 'Who', amount_pence: 'Price', inside_pence: 'Inside price',
  state: 'Done', payment_status: 'Paid', payment_method: 'Paid', confirmed: 'Day agreed', time_agreed_for: 'Time agreed',
  // Their page's tick (0079, tidy-up 2 item 3, 20 Oct 2026): a refused Undo of it read 'changed postcode enough to "1"'.
  postcode_enough: '"The postcode is enough"',
  // Their page's Commercial switch (0080, 21 Oct 2026).
  commercial: '"Commercial"',
}
const WAY_WORDS: Record<string, string> = { text: 'Text', whatsapp: 'WhatsApp', email: 'Email', none: "Don't message" }

// One box someone else changed, in words: "Lewis changed Phone to 07700 900123", "Liam cleared Notes".
export function theirWords(c: TheirChange): string {
  const who = c.by ?? 'Someone'
  const box = BOX_WORDS[c.field] ?? c.field.replace(/_/g, ' ')
  const v = c.value
  if (v === null || v === undefined || v === '') return `${who} cleared ${box}`
  if (c.field === 'to_call' || c.field === 'confirmed' || c.field === 'postcode_enough' || c.field === 'commercial') return `${who} ${v ? 'ticked' : 'unticked'} ${box}`
  let words: string
  if (/_pence$/.test(c.field) && typeof v === 'number') words = money(v)
  else if (c.field === 'date' && typeof v === 'string') words = dayLabel(v)
  else if (c.field === 'duration_minutes' && typeof v === 'number') words = `${v} minutes`
  else if (c.field === 'message_by' && typeof v === 'string') words = WAY_WORDS[v] ?? v
  else {
    // A note in quotes, on one line and cut short; a phone number, name or address as it is.
    const flat = String(v).replace(/\s*\n\s*/g, ' / ').trim()
    const short = flat.length > 60 ? `${flat.slice(0, 57)}...` : flat
    words = /^(phone|email|name|area|postcode|address)$/.test(c.field) ? short : `"${short}"`
  }
  return `${who} changed ${box} to ${words}`
}

export function chipItemState(m: QueuedMutation): string {
  // Said with what the other person changed it to, when the server said (Liam, 15 Oct 2026: "it then shows what they
  // changed it to").
  if (m.status === 'conflict' && m.theirs?.length) return `not saved: ${m.theirs.map(theirWords).join(' and ')} first`
  if (m.status === 'conflict') return 'not saved: it changed somewhere else first'
  if (m.status === 'failed') return `not saved: ${m.error ?? 'refused'}`
  if (m.status === 'signed-out') return 'held until you sign in again'
  if (m.status === 'parked') return WAITING_FOR_ACCOUNT
  if (m.status !== 'queued') return 'saving'
  if (m.error === BEHIND_EARLIER) return 'waiting for the change above'
  return m.error ? `waiting: ${m.error}` : 'waiting for signal'
}

// The toast for anything the SERVER decided on its own after a reconnect.
// Several can be true of one ack (a replayed log that also booked the next
// visit), so all of them are said rather than the first that matched.
export interface MutationNotice {
  restored?: boolean
  replayedClean?: boolean
  nextBooked?: { date: string } | null
  removedBookings?: { date: string }[]
  row?: { date?: string; amount_pence?: number | null }
  // Money brought from Squeegee (0043): what a save that waited in the queue did to it.
  money?: { kept_as_credit_pence?: number; receipt_pence?: number | null } | null
}

export function noticeLines(
  d: MutationNotice,
  fmt: { money: (p: number | null) => string; shortDate: (iso: string | null | undefined) => string; nextBookedMessage: (n: { date: string }) => string },
): string[] {
  const lines: string[] = []
  if (d.restored) {
    const at = typeof d.row?.amount_pence === 'number' ? ` at ${fmt.money(d.row.amount_pence)}` : ''
    lines.push(`Put the ${fmt.shortDate(d.row?.date ?? null)} clean back${at}`)
  } else if (d.replayedClean) {
    // The server recognised the idempotency key and answered "already done",
    // so none of the other flags are set — without this the clean that logged
    // itself on the first, unacknowledged attempt landed in silence.
    lines.push('Saved from an earlier attempt. Check the diary.')
  }
  if (d.nextBooked) lines.push(fmt.nextBookedMessage(d.nextBooked))
  const removed = d.removedBookings ?? []
  if (removed.length) {
    lines.push(
      `${removed.length} booked visit${removed.length === 1 ? '' : 's'} removed: ${removed.map((b) => fmt.shortDate(b.date)).join(', ')}`,
    )
  }
  // Cash taken over what was due is kept as credit; cash from an older app has no amount for the owner to confirm.
  if (d.money?.kept_as_credit_pence) lines.push(`${fmt.money(d.money.kept_as_credit_pence)} kept as credit for their next clean`)
  if (d.money && d.money.receipt_pence === null) lines.push('Paid cash, amount not recorded: confirm it on Owed')
  return lines
}
